import React, { useState } from "react";
import { Button } from "reactstrap";
import SinglePermission from "./SinglePermission";
import { toast } from "react-toastify";
import { SelectElement } from "@workspace/common";

// Import Images
import micIconImg from "./images/mic.png";
import cameraIconImg from "./images/camera.png";
import faceScannerIconImg from "./images/face-scanner.png";
import environmentImg from "./images/environment.png";
import connectionImg from "./images/connection.png";
import lightingImg from "./images/lighting.png";
import languageImg from "./images/language.png";

const languageSet = [
  {
    value: "english",
    label: "English",
  },
  {
    value: "chinese simplified",
    label: "Chinese",
  },
  {
    value: "german",
    label: "German",
  },
  {
    value: "french",
    label: "French",
  },
  {
    value: "dutch",
    label: "Dutch",
  },
];

const InterviewPermission = ({
  permissions,
  setPermissions,
  setStep,
  setLanguage,
  language,
}) => {
  // Local toggles for user intent
  const [micToggle, setMicToggle] = useState(false);
  const [cameraToggle, setCameraToggle] = useState(false);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);

  const cameraSubText = (
    <ul className="pb-0 mb-0">
      <li>Please allow access to your camera for video functionality.</li>
      <li>Turn on your camera to enable visual interaction.</li>
    </ul>
  );

  const requestPermissions = async () => {
    try {
      // Request both audio and video permissions from the browser
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });

      // Stop all tracks immediately so we are not actively using them yet
      stream.getTracks().forEach((track) => track.stop());
      setIsPermissionGranted(true);
    } catch (error) {
      console.error("Permission denied:", error);
      toast.error(
        "Please enable camera and microphone to proceed with the interview."
      );
      setIsPermissionGranted(false);
    }
  };

  const allTogglesOn = micToggle && cameraToggle;
  const browserPermissionsGranted =
    permissions.micPermission && permissions.cameraPermission;

  return (
    <div
      className="d-flex align-items-center justify-content-center pt-3"
      style={{
        backgroundColor: "white",
        height: "100vh",
      }}
    >
      <div>
        <div>
          <span className="fw-semibold" style={{ fontSize: "22px" }}>
            Permission & Checklist for Pre-Assignment Video Interview
          </span>
        </div>
        <div
          className="py-2 px-3 d-flex align-items-center justify-content-center gap-5"
          style={{
            backgroundColor: "white",
            borderRadius: "5px",
            boxShadow: "0px 0px 5px 0px #0000001a",
            padding: "10px",
          }}
        >
          <img
            src={languageImg}
            alt=""
            style={{ height: "35px", width: "35px" }}
          />
          <div
            className="flex-fill d-flex flex-column"
            style={{
              width: "min-content",
            }}
          >
            <span style={{ fontSize: "16px" }} className="fw-semibold p-0 m-0">
              Select your preferred language for the interview
            </span>
            <span
              className="p-0 m-0 mt-2 fs-5"
              style={{
                color: "#6c757d",
                fontSize: "13px",
              }}
            >
              The AI will communicate with you in the selected language.
            </span>
          </div>
          <div className="w-25">
            <SelectElement
              optionsData={languageSet}
              setSelectedOptionData={setLanguage}
              value={language}
            />
          </div>
        </div>

        <div className="mt-2">
          <SinglePermission
            iconImg={micIconImg}
            mainText="Enable Microphone for Interaction"
            subText="Please grant permission to access your microphone for seamless interaction."
            onPermissionChange={(e) => {
              setMicToggle(e.target.checked);
              setPermissions((prev) => ({
                ...prev,
                micPermission: e.target.checked,
              }));
              if (!e.target.checked) {
                setIsPermissionGranted(false);
              }
            }}
            permission={micToggle}
          />
        </div>
        <div className="mt-3">
          <SinglePermission
            iconImg={cameraIconImg}
            mainText="Enable Camera for Video Access"
            subText={cameraSubText}
            onPermissionChange={(e) => {
              setCameraToggle(e.target.checked);
              setPermissions((prev) => ({
                ...prev,
                cameraPermission: e.target.checked,
              }));
              if (!e.target.checked) {
                setIsPermissionGranted(false);
              }
            }}
            permission={cameraToggle}
          />
        </div>
        <div className="mt-3">
          <SinglePermission
            iconImg={faceScannerIconImg}
            mainText="Maintain Professional Posture and Focus"
            subText="Please ensure you are seated upright and keep your eyes on the camera for a professional appearance."
          />
        </div>
        <div className="mt-3">
          <SinglePermission
            iconImg={environmentImg}
            mainText="Set Up a Quiet Environment"
            subText="Choose a quiet location free from distractions and background noise to maintain focus."
          />
        </div>
        <div className="mt-3">
          <SinglePermission
            iconImg={connectionImg}
            mainText="Check Your Internet Connection"
            subText="Please ensure you have a stable internet connection to avoid disruptions during the interview."
          />
        </div>
        <div className="mt-3">
          <SinglePermission
            iconImg={lightingImg}
            mainText="Ensure Proper Lighting"
            subText="Position yourself in a well-lit area, with light coming from the front to clearly illuminate your face."
          />
        </div>
        <div className="mt-3">
          <p style={{ fontSize: "16px" }}>
            By enabling these permissions and following these guidelines, you
            help ensure a smooth and professional interview experience. Thank
            you!
          </p>
        </div>

        {/* Logic for displaying buttons */}
        {browserPermissionsGranted && !isPermissionGranted && (
          <>
            {allTogglesOn && (
              <div className="mb-4">
                <Button
                  className="w-100 d-flex align-items-center gap-2 justify-content-center mt-3"
                  style={{
                    height: "45px",
                    backgroundColor: "#0A4FAE",
                    color: "white",
                  }}
                  onClick={requestPermissions}
                >
                  <span className="fs-5">Allow Camera & Mic</span>
                </Button>
              </div>
            )}
          </>
        )}

        {browserPermissionsGranted && isPermissionGranted && (
          <div className="mb-4">
            <Button
              className="btn btn-success w-100 d-flex align-items-center gap-2 justify-content-center mt-3"
              style={{
                height: "45px",
              }}
              onClick={() => setStep(1)}
            >
              <span className="fs-5">Join</span>
              <i className="ri-arrow-right-line fs-5"></i>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewPermission;
